import { ILicenseAllowance, IServer, IServiceInstance } from '@module/license/models';
import { ISiteModel } from '@module/shared/components/Site/models';
import { AVAILABLE_OPERATIONAL_USAGES } from '@service/constants';
import moment from 'moment';

function getCommonHeaders(accessToken: string): Record<string, string> {
  return {
    'Authorization': `Bearer ${accessToken}`,
    'Accept': 'application/json',
    'Content-Type': 'application/json'
  };
}

function get(uri: string, accessToken: string) {
  const init: RequestInit = {
    method: 'GET',
    headers: getCommonHeaders(accessToken)
  };

  return fetch(uri, init)
    .then(data => data.json());
}

function getStream(uri: string, accessToken: string) {
  const init: RequestInit = {
    method: 'GET',
    headers: {
      'Authorization': `Bearer ${accessToken}`,
      'Accept': 'application/json',
      'Content-Type': 'application/octet-stream'
    }
  };

  return fetch(uri, init)
    .then(data => data);
}

function post(uri: string, body: any, accessToken: string) {
  const postBody: RequestInit = {
    method: 'POST',
    headers: getCommonHeaders(accessToken),
    body
  };
  const response = fetch(uri, postBody);
  return response;
}

function put(uri: string, body: any, accessToken: string) {
  const putBody: RequestInit = {
    method: 'PUT',
    headers: getCommonHeaders(accessToken),
    body
  };
  const response = fetch(uri, putBody);
  return response;
}

function remove(uri: string, accessToken: string) {
  const body: RequestInit = {
    method: 'DELETE',
    headers: getCommonHeaders(accessToken)
  };
  const response = fetch(uri, body);
  return response;
}

function toServerBody(server: IServer) {
  return JSON.stringify({
    id: server.id,
    serverName: server.description.value,
    siteId: server.siteId,
    selectedOperationalUsage: server.operationalUseType,
    availableOperationalUsages: AVAILABLE_OPERATIONAL_USAGES,
    hardwareId: server.serverId.value,
    discriminator: 'ConfigurableServer'
  });
}

// TODO: Modify this to pass all relevant data when getting the info from servis
function toSiteBody(site: ISiteModel) {
  return JSON.stringify({
    servIsId: site.servIsId,
    siteName: site.name,
    owner: site.customer,
    regionName: site.region,
  });
}

function toServiceInstanceBody(serviceInstance: IServiceInstance) {
  return JSON.stringify({
    allocatedAssets: serviceInstance.allocatedAssets.value,
    siteId: serviceInstance.siteId,
    serverId: serviceInstance.serverId,
    id: serviceInstance.id,
    selectedService: serviceInstance.selectedServiceType,
    discriminator: 'ConfigurableService'
  });
}

function toAllocationBody(allocation: ILicenseAllowance) {
  return JSON.stringify({
    id: allocation.id,
    siteId: allocation.siteId,
    licensableService: allocation.licensableService,
    site: allocation.site, // this is null right now on all the api calls and doesn't need to be on this object because it is already part of licensable service
    poNumber: allocation.productOrderNumber.value,
    totalAssetAllocation: allocation.totalAssetAllocation.value,
    expirationDateUtc: moment(allocation.expirationDateUtc.value),
    discriminator: 'ConfigurableAllocation'
  });
}

export class SiteApiClient {
  constructor(private baseUri: string) { }

  getOperationalUsages(accessToken: string) {
    return get(`${this.baseUri}Servers/OperationalUsages`, accessToken);
  }

  getAllServers(siteId: number, accessToken: string) {
    return get(`${this.baseUri}Sites/${siteId}/Servers/Allowances`, accessToken);
  }

  getServIsSite(servIsId: string, accessToken: string) {
    return get(`${this.baseUri}Sites/GetSite?servIsId=${servIsId}`, accessToken);
  }

  registerSite(site: ISiteModel, accessToken: string) {
    return post(`${this.baseUri}Sites`, toSiteBody(site), accessToken);
  }

  updateSite(siteId: number, servIsId: string, accessToken: string) {
    return put(`${this.baseUri}Sites?id=${siteId}&servIsId=${servIsId}`, '', accessToken);
  }

  updateSiteDetails(site: any, accessToken: string) {
    return put(`${this.baseUri}Sites/Details`, JSON.stringify({
      id: site.id,
      servIsId: site.servIsId,
      siteName: site.siteName,
      owner: site.owner,
      operator: site.operator,
      regionName: site.regionName,
      country: site.country,
      city: site.city,
      registered: true
    }), accessToken);
  }

  addServer(server: IServer, accessToken: string) {
    return post(`${this.baseUri}Sites/${server.siteId}/Servers`, toServerBody(server), accessToken);
  }

  updateServer(server: IServer, accessToken: string) {
    return put(`${this.baseUri}Sites/${server.siteId}/Servers/${server.id}`, toServerBody(server), accessToken);
  }

  deleteServer(server: IServer, accessToken: string) {
    return remove(`${this.baseUri}Sites/${server.siteId}/Servers/${server.id}`, accessToken);
  }

  deleteService(serviceInstance: IServiceInstance, accessToken: string) {
    return remove(`${this.baseUri}Sites/${serviceInstance.siteId}/Servers/${serviceInstance.serverId}/Services/${serviceInstance.id}`, accessToken);
  }

  addService(serviceInstance: IServiceInstance, accessToken: string) {
    return post(`${this.baseUri}Sites/${serviceInstance.siteId}/Servers/${serviceInstance.serverId}/Services`, toServiceInstanceBody(serviceInstance), accessToken);
  }

  updateService(serviceInstance: IServiceInstance, accessToken: string) {
    return put(`${this.baseUri}Sites/${serviceInstance.siteId}/Servers/${serviceInstance.serverId}/Services/${serviceInstance.id}`, toServiceInstanceBody(serviceInstance), accessToken);
  }

  getAllServices(siteId: number, accessToken: string) {
    return get(`${this.baseUri}Sites/${siteId}/SiteAllocations`, accessToken);
  }

  addAllocation(allocation: ILicenseAllowance, accessToken: string) {
    return post(`${this.baseUri}Sites/${allocation.siteId}/SiteAllocations`, toAllocationBody(allocation), accessToken);
  }

  updateAllocation(allocation: ILicenseAllowance, accessToken: string) {
    return put(`${this.baseUri}Sites/${allocation.siteId}/SiteAllocations/${allocation.id}`, toAllocationBody(allocation), accessToken);
  }

  deleteAllocation(allocation: ILicenseAllowance, accessToken: string) {
    return remove(`${this.baseUri}Sites/${allocation.siteId}/SiteAllocations/${allocation.id}`, accessToken);
  }

  getNewAllocation(siteId: number, accessToken: string) {
    return get(`${this.baseUri}Sites/${siteId}/SiteAllocations/New`, accessToken);
  }

  getLicenseFile(siteId: number, accessToken: string) {
    return getStream(`${this.baseUri}Sites/${siteId}/SiteLicenses`, accessToken);
  }
}

export default SiteApiClient;
